import { CaretDown, Check } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Checkbox multi-select over a fixed option list.
 *
 * Generalised from FeedPartnerSelect, which had this exact behaviour
 * hard-wired to feed partners. Same interaction rules, so the Reports
 * filter bar stays consistent across all four of its dropdowns:
 *   • empty selection === "all" (no filter sent)
 *   • ticking every option collapses back to "all" rather than sending
 *     an N-item list that means the same thing
 *   • the menu stays open while ticking, so several boxes can be set
 *     in one pass
 *
 * `value` is an array of selected values; `onChange` receives an array.
 */
function VisibleCheckbox({ checked }) {
  return (
    <span
      aria-hidden="true"
      className={`mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border ${
        checked
          ? "border-[#002FA7] bg-[#002FA7] text-white"
          : "border-slate-400 bg-white"
      }`}
    >
      {checked ? <Check size={11} weight="bold" /> : null}
    </span>
  );
}

export function multiLabel(selected, options, allLabel) {
  if (!selected.length) return allLabel;
  if (selected.length === 1) {
    const hit = options.find((o) => o.value === selected[0]);
    return hit?.label || selected[0];
  }
  return `${selected.length} selected`;
}

export default function MultiSelect({
  value = [],
  onChange,
  options = [],
  allLabel = "All",
  menuLabel = "",
  testId,
  className = "w-[200px]",
  maxHeight = "max-h-80",
}) {
  const selected = Array.isArray(value) ? value : [];
  const allChecked = selected.length === 0;

  const emit = (next) => {
    const uniq = [...new Set(next)];
    // Every box ticked is the same as no filter — send the cheaper query.
    // Guarded on options.length: while the list is still loading it is 0,
    // and `uniq.length >= 0` would silently discard every selection.
    const everythingTicked = options.length > 0 && uniq.length >= options.length;
    onChange?.(everythingTicked ? [] : uniq);
  };

  const toggle = (v) => {
    if (selected.includes(v)) emit(selected.filter((x) => x !== v));
    else emit([...selected, v]);
  };

  const label = multiLabel(selected, options, allLabel);
  const title = selected.length
    ? selected.map((v) => options.find((o) => o.value === v)?.label || v).join(", ")
    : allLabel;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={`h-9 justify-between font-normal text-sm ${className}`}
          data-testid={testId}
          title={title}
        >
          <span className="min-w-0 truncate">{label}</span>
          <CaretDown size={12} className="ml-2 opacity-50 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={`w-64 ${maxHeight} overflow-y-auto`}
        data-testid={testId ? `${testId}-menu` : undefined}
      >
        {menuLabel ? (
          <>
            <DropdownMenuLabel>{menuLabel}</DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={(e) => { e.preventDefault(); onChange?.([]); }}
          data-testid={testId ? `${testId}-all` : undefined}
        >
          <VisibleCheckbox checked={allChecked} />
          {allLabel}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {options.map((opt) => (
          <DropdownMenuItem
            key={opt.value}
            className="cursor-pointer"
            onSelect={(e) => { e.preventDefault(); toggle(opt.value); }}
            data-testid={testId ? `${testId}-${opt.value}` : undefined}
          >
            <VisibleCheckbox checked={selected.includes(opt.value)} />
            <span className="min-w-0 truncate">{opt.label}</span>
            {opt.hint ? (
              <span className="text-slate-400 font-mono ml-2 text-[11px] shrink-0">{opt.hint}</span>
            ) : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
